import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del, post, put } from '@/shared/api/api-client';
import type {
  ResourceWorkingHours,
  ResourceWorkingHoursInput,
} from '@/types/resource-schedule-api';
import { SCHEDULE_OWN_ERROR_HANDLING } from './use-resource-calendar-settings';
import {
  resourceScheduleQueryKey,
  resourceScheduleUrl,
  toResourceScheduleError,
} from './use-resource-schedule';

function workingHoursUrl(resourceId: string, id?: string): string {
  const base = `${resourceScheduleUrl(resourceId)}/WorkingHours`;
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

/**
 * Add a working-hours profile to a device — PROD-2007.
 *
 *   POST /api/Resource/{resourceId}/Schedule/WorkingHours  -> 201
 *   body: { profileName, type: "work"|"vacation", startDate, endDate|null,
 *           isActive, details: [{ dayOfWeek, startTime, endTime, dayOff }] }
 *
 * Server rules the dialog pre-validates: times are `"HH:mm"` wall clock, end
 * after start, no overnight; one detail per weekday; Work needs >= 1 working
 * weekday and may leave `endDate` null; Vacation needs an `endDate` and an
 * empty `details`. A split shift is two Work profiles.
 */
export function useCreateResourceWorkingHours(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResourceWorkingHoursInput) => {
      try {
        return await post<ResourceWorkingHours>(workingHoursUrl(resourceId), input);
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}

export interface UpdateResourceWorkingHoursInput {
  id: string;
  input: ResourceWorkingHoursInput;
}

/**
 * Replace one working-hours profile — same body as create.
 *
 *   PUT /api/Resource/{resourceId}/Schedule/WorkingHours/{id}  -> 200
 *
 * To keep a profile but have the scheduler ignore it, PUT `isActive: false`
 * rather than deleting it.
 */
export function useUpdateResourceWorkingHours(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: UpdateResourceWorkingHoursInput) => {
      try {
        return await put<ResourceWorkingHours>(workingHoursUrl(resourceId, id), input);
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}

/**
 * Hard-delete one working-hours profile.
 *
 *   DELETE /api/Resource/{resourceId}/Schedule/WorkingHours/{id}  -> 204
 */
export function useDeleteResourceWorkingHours(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await del<void>(workingHoursUrl(resourceId, id));
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}
